import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FaBars, FaTimes, FaBell, FaChevronDown } from 'react-icons/fa';
import { logout } from '../store/authSlice';
import { fetchNotifications, markNotificationRead, markAllNotificationsRead, fetchUnreadCount, Notification } from '../store/notificationsSlice';
import { RootState, AppDispatch } from '../store';

const Navbar = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const bellButtonRef = useRef<HTMLButtonElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const { notifications, unreadCount } = useSelector((state: RootState) => state.notifications);

  // Fetch unread count on mount + every 60s
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchUnreadCount());
      const interval = setInterval(() => dispatch(fetchUnreadCount()), 60000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, dispatch]);

  // Close dropdowns when clicking outside or pressing Escape
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setBellOpen(false);
      }
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (bellOpen) {
          setBellOpen(false);
          bellButtonRef.current?.focus();
        }
        if (moreOpen) {
          setMoreOpen(false);
          moreButtonRef.current?.focus();
        }
        if (menuOpen) {
          setMenuOpen(false);
          menuToggleRef.current?.focus();
        }
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [bellOpen, moreOpen, menuOpen]);

  const handleBellClick = () => {
    if (!bellOpen) {
      dispatch(fetchNotifications({}));
    }
    setBellOpen(!bellOpen);
  };

  const handleNotificationClick = (n: Notification) => {
    if (!n.is_read) {
      dispatch(markNotificationRead(n.id));
    }
    setBellOpen(false);
    if (n.link) {
      navigate(n.link);
    }
  };

  const handleMarkAllRead = () => {
    dispatch(markAllNotificationsRead());
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
    setMenuOpen(false);
  };

  const closeMenu = () => {
    setMenuOpen(false);
    menuToggleRef.current?.focus();
  };

  // Focus first link in mobile menu when it opens; trap focus inside when open
  useEffect(() => {
    if (menuOpen && mobileMenuRef.current) {
      const firstLink = mobileMenuRef.current.querySelector<HTMLElement>('a, button');
      if (firstLink) firstLink.focus();
    }
  }, [menuOpen]);

  // Focus trap: keep Tab cycling within mobile menu when open
  useEffect(() => {
    if (!menuOpen || !mobileMenuRef.current) return;
    const menu = mobileMenuRef.current;
    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = menu.querySelectorAll<HTMLElement>('a, button');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [menuOpen]);

  // Arrow key navigation in More dropdown (ARIA menu pattern)
  const handleMoreKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!moreMenuRef.current) return;
    const items = moreMenuRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]');
    if (!items.length) return;
    const current = Array.from(items).indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[(current + 1) % items.length].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[(current - 1 + items.length) % items.length].focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1].focus();
    }
  }, []);

  const linkClass = (path: string) =>
    `${location.pathname === path ? 'text-blue-600 font-semibold' : 'text-gray-700'} hover:text-blue-600 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded`;

  const ariaCurrent = (path: string): { 'aria-current'?: 'page' } =>
    location.pathname === path ? { 'aria-current': 'page' } : {};

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <nav className="bg-white shadow-sm border-b border-gray-100" aria-label="Main navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to={isAuthenticated ? (user?.role === 'employer' ? '/employer' : '/dashboard') : '/'} className="inline-flex min-h-11 items-center text-2xl font-bold text-blue-600">
            StageCrew
          </Link>

          {/* Desktop */}
          <div className="hidden md:flex items-center space-x-6">
            {isAuthenticated ? (
              <>
                {user?.role === 'employer' ? (
                  <>
                    <Link to="/employer" className={linkClass('/employer')} {...ariaCurrent('/employer')}>Dashboard</Link>
                    <Link to="/post-gig" className={linkClass('/post-gig')} {...ariaCurrent('/post-gig')}>Post a Gig</Link>
                    <Link to="/jobs" className={linkClass('/jobs')} {...ariaCurrent('/jobs')}>Browse Jobs</Link>
                    <Link to="/messages" className={linkClass('/messages')} {...ariaCurrent('/messages')}>Messages</Link>
                    <Link to="/payments" className={linkClass('/payments')} {...ariaCurrent('/payments')}>Payments</Link>
                    <Link to="/employer/subscription" className={linkClass('/employer/subscription')} {...ariaCurrent('/employer/subscription')}>Plan</Link>
                    <Link to="/employer/setup" className={linkClass('/employer/setup')} {...ariaCurrent('/employer/setup')}>Company</Link>
                    <Link to="/settings" className={linkClass('/settings')} {...ariaCurrent('/settings')}>Settings</Link>
                    <Link to="/help" className={linkClass('/help')} {...ariaCurrent('/help')}>Help</Link>
                  </>
                ) : (
                  <>
                    <Link to="/dashboard" className={linkClass('/dashboard')} {...ariaCurrent('/dashboard')}>Dashboard</Link>
                    <Link
                      to="/matches?status=matched"
                      className={`${['/matches', '/jobs', '/saved-jobs'].includes(location.pathname) ? 'text-blue-600 font-semibold' : 'text-gray-700'} hover:text-blue-600 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded`}
                      {...(location.pathname === '/matches' ? { 'aria-current': 'page' as const } : {})}
                    >
                      Find Work
                    </Link>
                    <Link to="/subscription" className={linkClass('/subscription')} {...ariaCurrent('/subscription')}>Plan</Link>
                    <Link to="/profile/setup" className={linkClass('/profile/setup')} {...ariaCurrent('/profile/setup')}>Profile</Link>
                    <div className="relative" ref={moreRef}>
                      <button
                        ref={moreButtonRef}
                        onClick={() => setMoreOpen(!moreOpen)}
                        aria-haspopup="true"
                        aria-expanded={moreOpen}
                        aria-controls="more-menu"
                        className={`flex items-center gap-1 hover:text-blue-600 transition ${
                          ['/insights', '/availability', '/referrals', '/payments', '/settings', '/help'].includes(location.pathname)
                            ? 'text-blue-600 font-semibold'
                            : 'text-gray-700'
                        }`}
                      >
                        More <FaChevronDown className="text-xs" aria-hidden="true" />
                      </button>
                      {moreOpen && (
                        <div id="more-menu" ref={moreMenuRef} onKeyDown={handleMoreKeyDown} className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1" role="menu" aria-label="More options">
                          <Link to="/payments" onClick={() => setMoreOpen(false)} role="menuitem" tabIndex={0} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 focus:bg-gray-50 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">Payments</Link>
                          <Link to="/insights" onClick={() => setMoreOpen(false)} role="menuitem" tabIndex={-1} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 focus:bg-gray-50 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">Legacy Insights</Link>
                          <Link to="/availability" onClick={() => setMoreOpen(false)} role="menuitem" tabIndex={-1} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 focus:bg-gray-50 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">Calendar</Link>
                          <Link to="/referrals" onClick={() => setMoreOpen(false)} role="menuitem" tabIndex={-1} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 focus:bg-gray-50 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">Referrals</Link>
                          <Link to="/vendors" onClick={() => setMoreOpen(false)} role="menuitem" tabIndex={-1} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 focus:bg-gray-50 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">Vendors</Link>
                          <div className="border-t border-gray-100 my-1" />
                          <Link to="/settings" onClick={() => setMoreOpen(false)} role="menuitem" tabIndex={-1} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 focus:bg-gray-50 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">Settings</Link>
                          <Link to="/help" onClick={() => setMoreOpen(false)} role="menuitem" tabIndex={-1} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 focus:bg-gray-50 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">Help</Link>
                        </div>
                      )}
                    </div>
                  </>
                )}
                {user?.role === 'admin' && (
                  <Link to="/admin" className={linkClass('/admin')} {...ariaCurrent('/admin')}>Admin</Link>
                )}

                {/* Notification Bell */}
                <div className="relative" ref={bellRef}>
                  <button
                    ref={bellButtonRef}
                    onClick={handleBellClick}
                    className="relative text-gray-500 hover:text-blue-600 transition"
                    aria-label="Notifications"
                    aria-haspopup="true"
                    aria-expanded={bellOpen}
                    aria-controls="notifications-panel"
                  >
                    <FaBell size={18} aria-hidden="true" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center" aria-live="polite" aria-atomic="true">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {bellOpen && (
                    <div id="notifications-panel" className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50" role="region" aria-label="Notifications panel">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                        <h3 className="font-semibold text-gray-900 text-sm">Notifications</h3>
                        {unreadCount > 0 && (
                          <button onClick={handleMarkAllRead} className="text-xs text-blue-600 hover:underline">
                            Mark all read
                          </button>
                        )}
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="px-4 py-8 text-center text-gray-400 text-sm">
                            No notifications yet
                          </div>
                        ) : (
                          notifications.slice(0, 10).map((n) => (
                            <button
                              key={n.id}
                              onClick={() => handleNotificationClick(n)}
                              className={`w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-50 transition ${!n.is_read ? 'bg-blue-50' : ''}`}
                            >
                              <div className="flex items-start gap-2">
                                {!n.is_read && (
                                  <span className="mt-1.5 w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                                )}
                                <div className={!n.is_read ? '' : 'ml-4'}>
                                  <p className="text-sm font-medium text-gray-900">{n.title}</p>
                                  {n.message && (
                                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                                  )}
                                  <p className="text-xs text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                                </div>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                      <Link
                        to="/notifications"
                        onClick={() => setBellOpen(false)}
                        className="block text-center text-sm text-blue-600 hover:text-blue-800 font-medium py-2 border-t border-gray-100"
                      >
                        View all notifications
                      </Link>
                    </div>
                  )}
                </div>

                <button onClick={handleLogout} className="text-gray-500 hover:text-red-600 text-sm transition">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/jobs" className={linkClass('/jobs')} {...ariaCurrent('/jobs')}>Browse Jobs</Link>
                <Link to="/for-workers" className={linkClass('/for-workers')} {...ariaCurrent('/for-workers')}>For Workers</Link>
                <Link to="/for-employers" className={linkClass('/for-employers')} {...ariaCurrent('/for-employers')}>For Employers</Link>
                <Link to="/vendors" className={linkClass('/vendors')} {...ariaCurrent('/vendors')}>Vendors</Link>
                <Link to="/pricing" className={linkClass('/pricing')} {...ariaCurrent('/pricing')}>Pricing</Link>
                <Link to="/blog" className={linkClass('/blog')} {...ariaCurrent('/blog')}>Blog</Link>
                <Link to="/help" className={linkClass('/help')} {...ariaCurrent('/help')}>Help</Link>
                <Link to="/login" className="text-gray-700 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded" {...ariaCurrent('/login')}>Login</Link>
                <Link to="/register?role=employer" className="bg-orange-500 text-white px-5 py-2 rounded-lg font-medium hover:bg-orange-600 transition focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2">
                  Request Crew
                </Link>
                <Link to="/register" className="bg-blue-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-blue-700 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" {...ariaCurrent('/register')}>
                  Sign Up Free
                </Link>
              </>
            )}
          </div>

          <button
            ref={menuToggleRef}
            className="flex h-11 w-11 items-center justify-center rounded text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 md:hidden"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav-menu"
          >
            {menuOpen ? <FaTimes size={22} aria-hidden="true" /> : <FaBars size={22} aria-hidden="true" />}
          </button>
        </div>

        {menuOpen && (
          <div id="mobile-nav-menu" ref={mobileMenuRef} className="space-y-1 pb-4 [&_[role=menuitem]]:min-h-11 md:hidden" role="menu" aria-label="Mobile navigation">
            {isAuthenticated ? (
              <>
                {user?.role === 'employer' ? (
                  <>
                    <Link to="/employer" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/employer')}>Dashboard</Link>
                    <Link to="/post-gig" onClick={closeMenu} role="menuitem" className="block py-2 text-orange-600 font-semibold hover:text-orange-700 focus:text-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 rounded" {...ariaCurrent('/post-gig')}>Post a Gig</Link>
                    <Link to="/jobs" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/jobs')}>Browse Jobs</Link>
                    <Link to="/messages" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/messages')}>Messages</Link>
                    <Link to="/payments" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/payments')}>Payments</Link>
                    <Link to="/employer/subscription" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/employer/subscription')}>Plan</Link>
                    <Link to="/employer/setup" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/employer/setup')}>Company Profile</Link>
                    <Link to="/settings" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/settings')}>Settings</Link>
                    <Link to="/help" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/help')}>Help</Link>
                  </>
                ) : (
                  <>
                    <Link to="/dashboard" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/dashboard')}>Dashboard</Link>
                    <Link to="/matches?status=matched" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/matches')}>Find Work</Link>
                    <Link to="/payments" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/payments')}>Payments</Link>
                    <Link to="/subscription" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/subscription')}>Plan</Link>
                    <Link to="/profile/setup" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/profile/setup')}>Profile</Link>
                    <Link to="/insights" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/insights')}>Legacy Insights</Link>
                    <Link to="/availability" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/availability')}>Calendar</Link>
                    <Link to="/referrals" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/referrals')}>Referrals</Link>
                    <Link to="/vendors" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/vendors')}>Vendors</Link>
                    <Link to="/settings" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/settings')}>Settings</Link>
                    <Link to="/help" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/help')}>Help</Link>
                  </>
                )}
                {user?.role === 'admin' && (
                  <Link to="/admin" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/admin')}>Admin</Link>
                )}
                <button onClick={handleLogout} role="menuitem" className="block w-full text-left py-2 text-red-600 mt-2 focus:outline-none focus:ring-2 focus:ring-red-500 rounded">Logout</button>
              </>
            ) : (
              <>
                <Link to="/jobs" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/jobs')}>Browse Jobs</Link>
                <Link to="/for-workers" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/for-workers')}>For Workers</Link>
                <Link to="/for-employers" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/for-employers')}>For Employers</Link>
                <Link to="/vendors" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/vendors')}>Vendors</Link>
                <Link to="/pricing" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/pricing')}>Pricing</Link>
                <Link to="/blog" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/blog')}>Blog</Link>
                <Link to="/login" onClick={closeMenu} role="menuitem" className="block py-2 text-gray-700 hover:text-blue-600 focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded" {...ariaCurrent('/login')}>Login</Link>
                <Link to="/register?role=employer" onClick={closeMenu} role="menuitem" className="block bg-orange-500 text-white px-4 py-2 rounded mt-2 text-center focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2">Request Crew</Link>
                <Link to="/register" onClick={closeMenu} role="menuitem" className="block bg-blue-600 text-white px-4 py-2 rounded mt-2 text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" {...ariaCurrent('/register')}>Sign Up Free</Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
